import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Volume2, VolumeX, Play, Pause, Sparkles } from "lucide-react";

const uid = () => "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
const VOICE_KEY = "dcl_companion_voice";
const GUESS_KEY = "dcl_companion_guess";

const Companion = () => {
    const [sessionId] = useState(() => {
        const key = "dcl_companion_session";
        let s = localStorage.getItem(key);
        if (!s) { s = uid(); localStorage.setItem(key, s); }
        return s;
    });
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem(VOICE_KEY) === "1");
    const [audioLoadingFor, setAudioLoadingFor] = useState(null);
    const [playingIdx, setPlayingIdx] = useState(null);
    const [audioUrls, setAudioUrls] = useState({}); // {msgIdx: blobUrl}
    const bottomRef = useRef(null);
    const audioRef = useRef(null);
    const urlCacheRef = useRef({});

    useEffect(() => {
        api.get(`/companion/history/${sessionId}`).then(r => setMessages(r.data));
    }, [sessionId]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    // Cleanup blob urls on unmount
    useEffect(() => () => {
        Object.values(urlCacheRef.current).forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
    }, []);

    const toggleVoice = () => {
        setVoiceOn(prev => {
            const v = !prev;
            localStorage.setItem(VOICE_KEY, v ? "1" : "0");
            if (!v && audioRef.current) { try { audioRef.current.pause(); } catch {} setPlayingIdx(null); }
            return v;
        });
    };

    const fetchAudio = async (msgIdx, text) => {
        if (urlCacheRef.current[msgIdx]) return urlCacheRef.current[msgIdx];
        setAudioLoadingFor(msgIdx);
        try {
            const res = await api.post("/tts/segment",
                { text, role: "companion", language: "en" },
                { responseType: "blob" });
            if (!res.data || (res.data.type && !res.data.type.startsWith("audio/"))) throw new Error("not-audio");
            const url = URL.createObjectURL(res.data);
            urlCacheRef.current[msgIdx] = url;
            setAudioUrls(prev => ({ ...prev, [msgIdx]: url }));
            return url;
        } catch {
            toast.error("His voice is resting", { description: "You may still read his reply." });
            return null;
        } finally { setAudioLoadingFor(null); }
    };

    const playMessage = async (msgIdx, text) => {
        const url = await fetchAudio(msgIdx, text);
        if (!url || !audioRef.current) return;
        audioRef.current.src = url;
        try {
            setPlayingIdx(msgIdx);
            await audioRef.current.play();
        } catch (err) {
            setPlayingIdx(null);
            if (err.name !== "AbortError") toast.error("Could not start audio", { description: "Please tap play again." });
        }
    };

    const pausePlayback = () => {
        try { audioRef.current?.pause(); } catch {}
        setPlayingIdx(null);
    };

    const send = async () => {
        const text = input.trim();
        if (!text || sending) return;
        setInput("");
        setSending(true);
        // Optimistic user message
        setMessages(m => [...m, { role: "user", content: text, created_at: new Date().toISOString() }]);
        try {
            const r = await api.post("/companion/chat", { session_id: sessionId, message: text });
            const reply = r.data.reply;
            setMessages(m => {
                const next = [...m, { role: "companion", content: reply, created_at: new Date().toISOString() }];
                if (voiceOn) {
                    // fire and forget — play once audio arrives
                    const newIdx = next.length - 1;
                    setTimeout(() => playMessage(newIdx, reply), 100);
                }
                return next;
            });
        } catch (e) {
            toast.error("The Companion is at prayer", { description: "Please try again shortly." });
        } finally { setSending(false); }
    };

    const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

    return (
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16" data-testid="companion-page">
            <div className="text-center mb-12">
                <div className="small-caps text-xs text-muted-foreground mb-3">A Friend from Scripture</div>
                <h1 className="font-serif text-4xl md:text-5xl">The Companion</h1>
                <p className="mt-6 max-w-xl mx-auto text-muted-foreground italic">
                    "A friend loves at all times, and a brother is born for adversity." — Proverbs 17:17
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                <aside className="md:col-span-4 space-y-6">
                    <div className="missal-card p-6 text-center">
                        <img src="/companion.jpg"
                             alt="The Companion — a friend from Scripture" className="w-full aspect-[3/4] object-cover border border-border" data-testid="companion-portrait" />
                        <div className="mt-5 small-caps text-[11px] accent-liturgy">He walks with you</div>
                        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                            He does not tell you his name. Not yet. He is a friend from the Scriptures — one voice among many, drawn near to walk this hour with you. Ask him anything.
                        </p>
                    </div>
                    <GuessCard sessionId={sessionId} />
                </aside>

                <section className="md:col-span-8">
                    <div className="missal-card min-h-[500px] flex flex-col">
                        {/* Voice toggle bar */}
                        <div className="border-b border-border px-6 py-3 flex items-center justify-between">
                            <div className="small-caps text-[10px] text-muted-foreground">Mode</div>
                            <button onClick={toggleVoice} data-testid="voice-toggle" aria-pressed={voiceOn}
                                className={`flex items-center gap-2 px-3 py-1.5 border text-xs small-caps transition-colors ${voiceOn ? 'border-liturgy accent-liturgy bg-liturgy/5' : 'border-border text-muted-foreground hover:border-liturgy'}`}>
                                {voiceOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
                                <span>{voiceOn ? "Text + Voice" : "Text only"}</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 space-y-8" data-testid="chat-window" style={{maxHeight: "60vh"}}>
                            {messages.length === 0 && (
                                <div className="text-center py-16 text-muted-foreground italic">
                                    Say anything. He listens.
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <div key={i} className="animate-fade-in" data-testid={`msg-${m.role}-${i}`}>
                                    <div className={`small-caps text-[10px] mb-2 flex items-center justify-between gap-2 ${m.role === "companion" ? "accent-liturgy" : "text-muted-foreground"}`}>
                                        <span>{m.role === "companion" ? "The Companion writes ~" : "You write ~"}</span>
                                        {m.role === "companion" && voiceOn && (
                                            <button
                                                onClick={() => (playingIdx === i ? pausePlayback() : playMessage(i, m.content))}
                                                disabled={audioLoadingFor === i}
                                                data-testid={`play-msg-${i}`}
                                                className="flex items-center gap-1.5 px-2 py-1 border border-transparent hover:border-liturgy text-[10px] disabled:opacity-40">
                                                {audioLoadingFor === i ? <span>…</span> :
                                                 playingIdx === i ? <><Pause size={10} /> <span>pause</span></> :
                                                 <><Play size={10} /> <span>hear him</span></>}
                                            </button>
                                        )}
                                    </div>
                                    <p className={`font-body leading-relaxed text-[15px] ${m.role === "companion" ? "" : "text-muted-foreground pl-4 border-l-2 border-border"}`}>
                                        {m.content}
                                    </p>
                                </div>
                            ))}
                            {sending && <div className="small-caps text-xs text-muted-foreground animate-pulse">He is thinking…</div>}
                            <div ref={bottomRef} />
                        </div>
                        <div className="border-t border-border p-4 flex items-end gap-3">
                            <textarea
                                value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKey}
                                data-testid="chat-input"
                                placeholder="Write your question, or simply a sigh."
                                rows={2}
                                className="flex-1 bg-transparent px-3 py-2 resize-none text-sm focus:outline-none"
                            />
                            <button onClick={send} disabled={sending || !input.trim()} data-testid="chat-send"
                                className="px-5 py-2 bg-liturgy text-white text-sm small-caps disabled:opacity-40">
                                Send
                            </button>
                        </div>
                    </div>
                    <p className="mt-4 text-xs text-muted-foreground text-center">
                        The Companion is here to accompany, not to replace: for sacraments, please see a priest; for medical or legal matters, please see a professional.
                    </p>
                    <audio ref={audioRef} onEnded={() => setPlayingIdx(null)} onError={() => setPlayingIdx(null)} />
                </section>
            </div>
        </div>
    );
};

export default Companion;

/**
 * A quiet card below the portrait. Visitors can wager one guess of who
 * The Companion might be. No confirmation is ever given.
 * The card remembers the last guess in localStorage so returning
 * visitors can revise once.
 */
const GuessCard = ({ sessionId }) => {
    const [guess, setGuess] = useState("");
    const [firstName, setFirstName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [previous, setPrevious] = useState(() => localStorage.getItem(GUESS_KEY) || "");
    const [total, setTotal] = useState(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        // Load a light leaderboard count so visitors sense the community
        api.get("/companion/guesses/leaderboard?limit=1").then(r => setTotal(r.data.total)).catch(() => {});
    }, [previous]);

    const submit = async (e) => {
        e.preventDefault();
        const g = guess.trim();
        if (!g) { toast.error("Give it a name"); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/companion/guess", { guess: g, session_id: sessionId, first_name: firstName.trim() || null });
            localStorage.setItem(GUESS_KEY, g);
            setPrevious(g);
            setGuess("");
            setTotal(r.data.total_guesses);
            toast.success("He smiles", { description: r.data.message });
        } catch {
            toast.error("Try again in a moment");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="missal-card p-6" data-testid="guess-card">
            <div className="small-caps text-[11px] accent-liturgy flex items-center gap-2">
                <Sparkles size={12} />
                Cast Your Guess
            </div>
            <p className="mt-2 text-xs text-muted-foreground italic leading-relaxed">
                Who do you think he is? A prophet, an apostle, a small unknown figure in the crowd? Your guess is never confirmed or denied — it waits with the others until the day he is revealed.
            </p>

            {previous && !expanded ? (
                <div className="mt-4 border-l-2 border-liturgy pl-3 py-1" data-testid="guess-previous">
                    <div className="small-caps text-[10px] text-muted-foreground">Your last guess</div>
                    <div className="font-serif text-lg accent-liturgy">{previous}</div>
                    <button onClick={() => setExpanded(true)} data-testid="guess-revise"
                        className="mt-2 text-[11px] small-caps text-muted-foreground hover:accent-liturgy">
                        Revise your guess →
                    </button>
                </div>
            ) : (
                <form onSubmit={submit} className="mt-4 space-y-3" data-testid="guess-form">
                    <input
                        type="text"
                        value={guess}
                        onChange={e => setGuess(e.target.value)}
                        data-testid="guess-input"
                        placeholder="A name from Scripture"
                        maxLength={120}
                        className="w-full bg-transparent border-b border-border px-1 py-2 text-sm font-body focus:outline-none focus:border-liturgy placeholder:italic placeholder:text-muted-foreground/50"
                    />
                    <input
                        type="text"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        data-testid="guess-first-name"
                        placeholder="Your first name (optional)"
                        maxLength={80}
                        className="w-full bg-transparent border-b border-border px-1 py-2 text-xs text-muted-foreground focus:outline-none focus:border-liturgy placeholder:italic"
                    />
                    <button type="submit" disabled={submitting || !guess.trim()} data-testid="guess-submit"
                        className="w-full py-2 bg-liturgy text-white small-caps text-xs disabled:opacity-40">
                        {submitting ? "Sending…" : "Whisper my guess"}
                    </button>
                </form>
            )}

            {total !== null && (
                <div className="mt-4 text-center small-caps text-[10px] text-muted-foreground" data-testid="guess-total">
                    {total} {total === 1 ? "guess so far" : "guesses so far"} · a community holding a small mystery
                </div>
            )}
        </div>
    );
};
